import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  useEstimateDeliveryFee,
  useGetAddressCoordinates,
  useGetGeoAddressInfo,
} from "../../../../../lib/services/geo-service";
import useDebounce from "../../../../common/useDebounce";
import { internationalCodes } from "../../../../../assets";
import styles from "../index.module.scss";
import ConvetiSelect from "./conveti-select";

const haversineDistance = ([lat1, lon1], [lat2, lon2]) => {
  const toRad = (x) => (x * Math.PI) / 180;

  const R = 6371; // Earth radius in km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return R * c; // in kilometers
};

const getResponseData = (response) => {
  return response?.data?.data || response?.data || response;
};

const getEstimateOptions = (estimateData) => {
  if (!estimateData) return [];

  if (Array.isArray(estimateData.options)) {
    return estimateData.options
      .filter((option) => option?.deliveryFee !== undefined)
      .map((option) => ({
        ...option,
        currency: option.currency || estimateData.currency,
        estimatedDelivery:
          option.estimatedDelivery || estimateData.estimatedDelivery,
        distance: option.distance || estimateData.distance,
        surge: option.surge || estimateData.surge,
        totalWeight: option.totalWeight || estimateData.totalWeight,
      }));
  }

  if (estimateData.deliveryFee !== undefined) {
    return [
      {
        ...estimateData,
        vehicleType: estimateData.vehicleType || "delivery",
      },
    ];
  }

  return [];
};

const formatVehicleType = (vehicleType) => {
  if (!vehicleType) return "Delivery";
  return vehicleType
    .toString()
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (value) => value.toUpperCase());
};

const formatDeliveryAmount = (amount, currency) => {
  const value = Number(amount || 0).toLocaleString();
  return currency ? `${currency} ${value}` : value;
};

const getItemWeightUnit = (item) => {
  if (typeof item?.weightUnit === "string") return item.weightUnit;
  return item?.weightUnit?.title || item?.weightUnit?.name || "kg";
};

const VehicleTypeImage = ({ vehicleType }) => {
  const type = (vehicleType || "").toLowerCase();

  if (type.includes("van") || type.includes("truck")) {
    return (
      <svg
        className="route-estimate-vehicle"
        viewBox="0 0 64 40"
        aria-hidden="true"
        focusable="false"
      >
        <rect x="5" y="13" width="32" height="16" rx="3" />
        <path d="M37 17h10l8 7v5H37z" />
        <path d="M43 18v6h8" />
        <circle cx="18" cy="31" r="5" />
        <circle cx="47" cy="31" r="5" />
        <path d="M11 13V9h20v4" />
      </svg>
    );
  }

  if (type.includes("car")) {
    return (
      <svg
        className="route-estimate-vehicle"
        viewBox="0 0 64 40"
        aria-hidden="true"
        focusable="false"
      >
        <path d="M12 25h40l-4-10H20z" />
        <path d="M16 25v5h32v-5" />
        <path d="M23 15l3-5h13l3 5" />
        <circle cx="21" cy="31" r="5" />
        <circle cx="43" cy="31" r="5" />
        <path d="M27 17h10" />
      </svg>
    );
  }

  if (type.includes("bike") || type.includes("motorcycle")) {
    return (
      <svg
        className="route-estimate-vehicle"
        viewBox="0 0 64 40"
        aria-hidden="true"
        focusable="false"
      >
        <circle cx="17" cy="29" r="7" />
        <circle cx="48" cy="29" r="7" />
        <path d="M17 29h12l7-11h7l5 11" />
        <path d="M29 29l-6-12h-6" />
        <path d="M36 18l-5-5h-6" />
        <path d="M43 18h8" />
        <path d="M25 13h6" />
      </svg>
    );
  }

  return (
    <svg
      className="route-estimate-vehicle"
      viewBox="0 0 64 40"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="10" y="12" width="34" height="17" rx="4" />
      <path d="M44 18h8l5 5v6H44z" />
      <circle cx="21" cy="31" r="5" />
      <circle cx="48" cy="31" r="5" />
      <path d="M18 17h18" />
    </svg>
  );
};

const DeliveryMethod = ({
  onBack,
  accessPoint,
  onSuccess,
  deliveryInfo,
  deliveryRegion,
  activateBackButton,
  selectedMethod,
  setSelectedMethod,
  navLocation,
  setShowOrderDetails,
  cart,
}) => {
  useEffect(() => {
    setShowOrderDetails?.(true);

    return () => {
      setShowOrderDetails?.(false);
    };
  }, [setShowOrderDetails]);

  useEffect(() => {
    activateBackButton(true, () => {
      onBack();
    });
  }, []);

  const {
    inStorePurchase,
    pickUpPurchase,
    deliveryPurchase,
    enableRouteDeliveryAmountEstimate,
    applyRouteEstimateToOrder,
  } = accessPoint?.settings || {};
  const deliveryRegions = useMemo(
    () => (Array.isArray(deliveryRegion) ? deliveryRegion : []),
    [deliveryRegion],
  );
  const marketplaceDelivery = navLocation === "marketplace";
  const routeEstimateEnabled =
    marketplaceDelivery || enableRouteDeliveryAmountEstimate === true;
  const routeEstimateAppliesToOrder =
    marketplaceDelivery || applyRouteEstimateToOrder === true;
  const canUseDeliveryLocation =
    marketplaceDelivery || deliveryPurchase === true;
  const shouldUseRouteEstimate =
    selectedMethod === "LOCATION" &&
    routeEstimateEnabled &&
    (routeEstimateAppliesToOrder || deliveryRegions.length === 0);
  const pickupAddress =
    accessPoint?.settings?.pickUpLocation || accessPoint?.address || "";
  const currency = cart?.currency || cart?.paymentItem?.[0]?.currency || "";

  const defaultRegion =
    deliveryInfo?.deliveryRegion ||
    (deliveryRegions.length > 0
      ? `${deliveryRegions[0]["address"]}-${deliveryRegions[0]["amount"]}`
      : "");

  const [formData, setFormData] = useState({
    destinationDeliveryAddress:
      deliveryInfo?.destinationDeliveryAddress ||
      deliveryInfo?.deliveryAddress ||
      "",
    city: deliveryInfo?.city || "",
    state: deliveryInfo?.state || "",
    country: deliveryInfo?.country || "",
    ...(navLocation !== "marketplace" && {
      deliveryRegion: defaultRegion,
    }),
    note: deliveryInfo?.note || "",
    intCode: deliveryInfo?.intCode || internationalCodes[0]?.code,
    phone: deliveryInfo?.phone || "",
  });

  const [coordinates, setCoordinates] = useState([]);
  const [routeEstimate, setRouteEstimate] = useState(null);
  const [isResolvingRouteEstimate, setIsResolvingRouteEstimate] =
    useState(false);
  const routeEstimateRequestIdRef = useRef(0);
  const routeEstimateAbortRef = useRef(null);
  const [selectedRouteEstimate, setSelectedRouteEstimate] = useState(
    deliveryInfo?.routeDeliveryEstimate || null,
  );
  const [isCalculatingClosestRegion, setisCalculatingClosestRegion] =
    useState(false);
  const estimateOptions = useMemo(
    () => getEstimateOptions(routeEstimate),
    [routeEstimate],
  );
  const countryOptions = useMemo(
    () =>
      internationalCodes.map((item) => ({
        label: item.name,
        value: item.name,
      })),
    [],
  );
  const phoneCodeOptions = useMemo(
    () =>
      internationalCodes.map((code) => ({
        label: `${code.code} (${code.countryCode})`,
        value: code.code,
      })),
    [],
  );
  const { mutate: fetchCoordinates, isLoading: isFetching } =
    useGetAddressCoordinates({
      onSuccess: (data) => {
        const [lng, lat] = data.data.geoCode.location.coordinates;
        setCoordinates([lat, lng]);
      },
      onError: (error) => {
        console.log("Failed to get coordinates", error.message);
      },
    });
  const deliveryRegionOptions = useMemo(
    () =>
      deliveryRegions.map((region) => ({
        label: `${region.address} (${region.amount})`,
        value: `${region.address}-${region.amount}`,
        disabled: isFetching || isCalculatingClosestRegion,
      })),
    [deliveryRegions, isFetching, isCalculatingClosestRegion],
  );

  const { mutateAsync: getGeoAddressInfo } = useGetGeoAddressInfo({
    onError: (error) => {
      if (error?.name === "AbortError") return;
      console.log("Failed to get address info", error.message);
    },
  });

  const { mutateAsync: estimateDeliveryFee } = useEstimateDeliveryFee({
    onError: (error) => {
      if (error?.name === "AbortError") return;
      console.log("Failed to estimate delivery fee", error.message);
    },
  });
  const isLoadingRouteEstimate = isResolvingRouteEstimate;

  const [error, setError] = useState("");
  useEffect(() => {
    if (!formData.phone && deliveryInfo?.phone) {
      setFormData((prev) => ({
        ...prev,
        phone: deliveryInfo.phone,
      }));
    }

    if (!formData.phone && deliveryInfo?.intCode) {
      setFormData((prev) => ({
        ...prev,
        intCode: deliveryInfo.intCode,
      }));
    }
  }, [deliveryInfo?.phone, formData.phone, deliveryInfo?.intCode]);

  const isPhoneValid = (phone) => /^\d+$/.test(phone);

  const handleFormChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError("");
  }, []);

  useEffect(() => {
    if (marketplaceDelivery && selectedMethod !== "LOCATION") {
      setSelectedMethod("LOCATION");
    }
  }, [marketplaceDelivery, selectedMethod, setSelectedMethod]);

  const handleChange = useCallback(
    (e) => {
      if (marketplaceDelivery) {
        setSelectedMethod("LOCATION");
        return;
      }

      setSelectedMethod(e.target.value);
    },
    [marketplaceDelivery, setSelectedMethod],
  );

  const handleDeliverySubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!selectedMethod) {
        setError("Please select a delivery method.");
        return;
      }
      if (selectedMethod === "LOCATION") {
        if (!isPhoneValid(formData.phone)) {
          setError("Please enter a valid phone number.");
          return;
        }
        if (!formData.country) {
          setError("Please select a country.");
          return;
        }
        if (
          !shouldUseRouteEstimate &&
          navLocation !== "marketplace" &&
          deliveryRegions.length > 0 &&
          !formData.deliveryRegion
        ) {
          setError("Please select a delivery region.");
          return;
        }
        const processedFormData = {
          ...formData,
          deliveryAddress: formData.destinationDeliveryAddress,
          deliveryRegion: shouldUseRouteEstimate ? "" : formData.deliveryRegion,
          routeDeliveryEstimate:
            shouldUseRouteEstimate &&
            (selectedRouteEstimate ||
              (!routeEstimateAppliesToOrder && estimateOptions[0]))
              ? selectedRouteEstimate ||
                (!routeEstimateAppliesToOrder && estimateOptions[0])
              : undefined,
          routeDeliveryEstimateApplied:
            shouldUseRouteEstimate && routeEstimateAppliesToOrder,
          phone:
            formData.phone.length >= 11
              ? formData.phone.slice(1)
              : formData.phone,
        };
        if (routeEstimateAppliesToOrder && shouldUseRouteEstimate) {
          if (!selectedRouteEstimate?.deliveryFee) {
            setError("Please select a delivery estimate.");
            return;
          }
        }
        onSuccess(processedFormData, selectedMethod);
      } else {
        const processedFormData = {};
        onSuccess(processedFormData, selectedMethod);
      }
      setError("");
    },
    [
      deliveryRegions.length,
      formData,
      estimateOptions,
      navLocation,
      onSuccess,
      routeEstimateAppliesToOrder,
      selectedMethod,
      selectedRouteEstimate,
      shouldUseRouteEstimate,
    ],
  );
  const debouncedAddress = useDebounce(
    formData.destinationDeliveryAddress,
    1000,
  );
  const destinationEstimateKey = [
    formData.destinationDeliveryAddress,
    formData.city,
    formData.state,
    formData.country,
  ].join("|");
  const debouncedDestinationEstimateKey = useDebounce(
    destinationEstimateKey,
    1000,
  );
  const [
    debouncedDestinationAddress,
    debouncedCity,
    debouncedState,
    debouncedCountry,
  ] = debouncedDestinationEstimateKey.split("|");

  useEffect(() => {
    routeEstimateRequestIdRef.current += 1;
    routeEstimateAbortRef.current?.abort();
    routeEstimateAbortRef.current = null;
    setRouteEstimate(null);
    setSelectedRouteEstimate(null);
    setIsResolvingRouteEstimate(false);
  }, [destinationEstimateKey]);

  useEffect(() => {
    if (
      navLocation !== "marketplace" &&
      debouncedAddress &&
      deliveryRegions.length > 0 &&
      !shouldUseRouteEstimate
    ) {
      fetchCoordinates({ address: debouncedAddress });
    }
  }, [
    debouncedAddress,
    deliveryRegions,
    fetchCoordinates,
    navLocation,
    shouldUseRouteEstimate,
  ]);

  useEffect(() => {
    if (coordinates.length > 0 && deliveryRegions.length > 0) {
      setisCalculatingClosestRegion(true);
      const closestRegion = deliveryRegions.reduce((prev, curr) => {
        const currCoords = curr.location.coordinates;
        const prevCoords = prev.location.coordinates;

        const currDist = haversineDistance(coordinates, [
          currCoords[1],
          currCoords[0],
        ]);
        const prevDist = haversineDistance(coordinates, [
          prevCoords[1],
          prevCoords[0],
        ]);

        return currDist < prevDist ? curr : prev;
      });

      // Update the form region value with the closest match
      {
        navLocation !== "marketplace"
          ? setFormData((prev) => ({
              ...prev,
              deliveryRegion: `${closestRegion.address}-${closestRegion.amount}`,
            }))
          : null;
      }

      setisCalculatingClosestRegion(false);
    }
  }, [coordinates, deliveryRegions, navLocation]);

  useEffect(() => {
    let shouldIgnore = false;
    let abortController = null;

    const fetchRouteEstimate = async () => {
      if (
        !shouldUseRouteEstimate ||
        !pickupAddress?.trim() ||
        !debouncedDestinationAddress?.trim() ||
        !debouncedCountry?.trim()
      ) {
        setRouteEstimate(null);
        setSelectedRouteEstimate(null);
        setIsResolvingRouteEstimate(false);
        return;
      }

      const requestId = routeEstimateRequestIdRef.current;
      abortController = new AbortController();
      routeEstimateAbortRef.current = abortController;

      try {
        setIsResolvingRouteEstimate(true);
        const destinationAddress = [
          debouncedDestinationAddress,
          debouncedCity,
          debouncedState,
          debouncedCountry,
        ]
          .filter(Boolean)
          .join(", ");

        const [pickupResponse, destinationResponse] = await Promise.all([
          getGeoAddressInfo({
            address: pickupAddress,
            signal: abortController.signal,
          }),
          getGeoAddressInfo({
            address: destinationAddress,
            signal: abortController.signal,
          }),
        ]);

        if (shouldIgnore || requestId !== routeEstimateRequestIdRef.current) {
          return;
        }

        const pickupInfo = getResponseData(pickupResponse);
        const destinationInfo = getResponseData(destinationResponse);

        if (!pickupInfo?.address || !destinationInfo?.address) {
          setRouteEstimate(null);
          setSelectedRouteEstimate(null);
          return;
        }

        const estimateResponse = await estimateDeliveryFee({
          signal: abortController.signal,
          pickup: {
            address: pickupInfo.address,
            city: pickupInfo.city,
            state: pickupInfo.state,
            country: pickupInfo.country,
          },
          destination: {
            address: destinationInfo.address,
            city: destinationInfo.city,
            state: destinationInfo.state,
            country: destinationInfo.country,
          },
          currency,
          paymentItem: (cart?.paymentItem || []).map((item) => ({
            name: item.name || item.title || "Item",
            quantity: Number(item.quantity || item.unit || 1),
            weight: Number(item.weight || 0),
            weightUnit: getItemWeightUnit(item),
          })),
        });

        if (shouldIgnore || requestId !== routeEstimateRequestIdRef.current) {
          return;
        }

        const estimateData = getResponseData(estimateResponse);
        const options = getEstimateOptions(estimateData);
        const nextEstimate = {
          ...estimateData,
          options,
        };

        setRouteEstimate(nextEstimate);
        setSelectedRouteEstimate((current) => {
          if (!routeEstimateAppliesToOrder || options.length === 0) {
            return null;
          }

          return (
            options.find(
              (option) =>
                option.vehicleType === current?.vehicleType &&
                Number(option.deliveryFee) === Number(current?.deliveryFee),
              ) || options[0]
          );
        });
      } catch (error) {
        if (
          error?.name !== "AbortError" &&
          !shouldIgnore &&
          requestId === routeEstimateRequestIdRef.current
        ) {
          setRouteEstimate(null);
          setSelectedRouteEstimate(null);
        }
      } finally {
        if (!shouldIgnore && requestId === routeEstimateRequestIdRef.current) {
          setIsResolvingRouteEstimate(false);
        }
        if (routeEstimateAbortRef.current === abortController) {
          routeEstimateAbortRef.current = null;
        }
      }
    };

    fetchRouteEstimate();

    return () => {
      shouldIgnore = true;
      abortController?.abort();
    };
  }, [
    cart?.paymentItem,
    currency,
    debouncedCity,
    debouncedCountry,
    debouncedDestinationAddress,
    debouncedState,
    estimateDeliveryFee,
    getGeoAddressInfo,
    pickupAddress,
    routeEstimateAppliesToOrder,
    shouldUseRouteEstimate,
  ]);

  return (
    <>
      <div className={`${styles.convetiForm} auth-form-container`}>
        <h2>Delivery Method</h2>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleDeliverySubmit}>
          {
            <div className="radio-box">
              {navLocation !== "marketplace" && (
                <>
                  {inStorePurchase === true && (
                    <div className="radio-input">
                      <label htmlFor="in-store">
                        <input
                          type="radio"
                          id="in-store"
                          value="INSTORE"
                          name="deliveryMethod"
                          checked={selectedMethod === "INSTORE"}
                          onChange={handleChange}
                        />
                        In-store
                      </label>
                    </div>
                  )}

                  {pickUpPurchase === true && (
                    <div className="radio-input">
                      <label htmlFor="pick-up">
                        <input
                          type="radio"
                          id="pick-up"
                          value="PICKUP"
                          name="deliveryMethod"
                          checked={selectedMethod === "PICKUP"}
                          onChange={handleChange}
                        />
                        Pick-up
                      </label>
                      <p>{accessPoint.address}</p>
                    </div>
                  )}
                </>
              )}
              {canUseDeliveryLocation && !marketplaceDelivery && (
                <div className="radio-input">
                  <label htmlFor="delivery">
                    <input
                      type="radio"
                      id="delivery"
                      value="LOCATION"
                      name="deliveryMethod"
                      checked={selectedMethod === "LOCATION"}
                      onChange={handleChange}
                    />
                    Delivery
                  </label>
                </div>
              )}
            </div>
          }

          {selectedMethod === "LOCATION" && (
            <div className={styles.convetiDeliveryDetails}>
              <div className={`${styles.convetiThirdField} input-box`}>
                <label htmlFor="country">Country</label>
                <div className="input-text ">
                  <ConvetiSelect
                    id="country"
                    name="country"
                    ariaLabel="Select your Country"
                    value={formData.country}
                    onChange={handleFormChange}
                    placeholder="Select Country"
                    options={countryOptions}
                  />
                </div>
              </div>

              <div className={`${styles.convetiThirdField} input-box`}>
                <label htmlFor="state">State</label>
                <div className="input-text">
                  <input
                    type="text"
                    id="state"
                    name="state"
                    aria-label="Enter your State"
                    required
                    placeholder="State"
                    value={formData.state}
                    onChange={handleFormChange}
                  />
                </div>
              </div>
              <div className={`${styles.convetiThirdField} input-box`}>
                <label htmlFor="city">City</label>
                <div className="input-text">
                  <input
                    type="text"
                    id="city"
                    name="city"
                    aria-label="Enter your City"
                    required
                    placeholder="City"
                    value={formData.city}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              <div className={`${styles.convetiFieldFull} input-box`}>
                <label htmlFor="address">Delivery Address</label>
                <div className="input-text">
                  <input
                    type="text"
                    id="address"
                    name="destinationDeliveryAddress"
                    aria-label="Enter your Delivery Address"
                    required
                    placeholder="Delivery Address"
                    value={formData.destinationDeliveryAddress}
                    onChange={handleFormChange}
                  />
                </div>
              </div>

              {!shouldUseRouteEstimate &&
                navLocation !== "marketplace" &&
                deliveryRegions.length > 0 && (
                  <div className={`${styles.convetiRegionField} input-box`}>
                    <label htmlFor="region">Region:</label>
                    <div className="input-text select-div">
                      <ConvetiSelect
                        id="region"
                        name="deliveryRegion"
                        value={formData.deliveryRegion}
                        onChange={handleFormChange}
                        ariaLabel="Select Region"
                        disabled={isFetching || isCalculatingClosestRegion}
                        placeholder="Select Region"
                        options={deliveryRegionOptions}
                      />
                    </div>
                  </div>
                )}

              <div className={`${styles.convetiPhoneField} input-box`}>
                <label htmlFor="phone">Phone Number:</label>
                <div className="input-text">
                  <ConvetiSelect
                    id="intCode"
                    name="intCode"
                    value={formData.intCode}
                    onChange={handleFormChange}
                    ariaLabel="Select international code"
                    options={phoneCodeOptions}
                    className={styles.convetiPhoneCodeSelect}
                  />
                  <input
                    type="text"
                    id="phone"
                    name="phone"
                    aria-label="Enter your phone number"
                    value={formData.phone}
                    onChange={handleFormChange}
                    required
                    placeholder="Enter your phone number"
                  />
                </div>
              </div>

              {shouldUseRouteEstimate && pickupAddress?.trim() && (
                <div className="input-box route-estimate-section">
                  <div className="route-estimate-heading">
                    <h3>Estimated delivery</h3>
                    {!routeEstimateAppliesToOrder && (
                      <p>
                        This estimate is shown for guidance and will not be
                        added to the order.
                      </p>
                    )}
                  </div>

                  {isLoadingRouteEstimate && (
                    <div className="route-estimate-placeholder">
                      Getting delivery estimate...
                    </div>
                  )}

                  {!isLoadingRouteEstimate && estimateOptions.length > 0 && (
                    <div className="route-estimate-grid">
                      {estimateOptions.map((option) => {
                        const isSelected =
                          selectedRouteEstimate?.vehicleType ===
                            option.vehicleType &&
                          Number(selectedRouteEstimate?.deliveryFee) ===
                            Number(option.deliveryFee);

                        return (
                          <button
                            key={`${option.vehicleType}-${option.deliveryFee}`}
                            type="button"
                            className={`route-estimate-card ${
                              isSelected ? "selected" : ""
                            } ${
                              routeEstimateAppliesToOrder
                                ? "is-selectable"
                                : ""
                            }`}
                            onClick={() => {
                              if (routeEstimateAppliesToOrder) {
                                setSelectedRouteEstimate(option);
                              }
                            }}
                          >
                            {routeEstimateAppliesToOrder && (
                              <span className="route-estimate-radio">
                                {isSelected && (
                                  <span className="route-estimate-radio-dot" />
                                )}
                              </span>
                            )}
                            <VehicleTypeImage
                              vehicleType={option.vehicleType}
                            />
                            <span className="route-estimate-main">
                              <strong>
                                {formatVehicleType(option.vehicleType)}
                              </strong>
                              <span>
                                {option.estimatedDelivery ||
                                  routeEstimate?.estimatedDelivery ||
                                  "Estimated delivery"}
                                {option.distance?.km
                                  ? ` - ${Number(option.distance.km).toFixed(
                                      1,
                                    )} km`
                                  : ""}
                              </span>
                            </span>
                            <span className="route-estimate-amount">
                              {formatDeliveryAmount(
                                option.deliveryFee,
                                option.currency || currency,
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}

                  {!isLoadingRouteEstimate && estimateOptions.length === 0 && (
                    <div className="route-estimate-placeholder">
                      Delivery estimate is not available for this address.
                    </div>
                  )}
                </div>
              )}

              <div className={`${styles.convetiNoteField} input-box`}>
                <label htmlFor="note">Delivery Note:</label>
                <div className="input-text">
                  <textarea
                    id="note"
                    name="note"
                    aria-label="Note for the vendor"
                    placeholder="Note for the vendor..."
                    value={formData.note}
                    onChange={handleFormChange}
                    rows={4}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="login-container">
            <button
              type="submit"
              className="auth-submit-button"
              aria-label="Submit delivery method"
              disabled={isLoadingRouteEstimate}
            >
              {isLoadingRouteEstimate
                ? "Getting delivery estimate..."
                : "Continue"}
            </button>
          </div>
        </form>
      </div>
    </>
  );
};

export default DeliveryMethod;
